import {
  createFieldMappings,
  CrmField,
  defaultMappingCreationV2Organization,
  fetchFormIoFieldsV2FieldMappings,
  listFieldMappings
} from '@goldcast/api/backend';
import logger from '@goldcast/logger';

import router from '@/router';
import { parseErrorFromServer, parseFieldMappingRequestBody } from '@/studio/orgIntegration/store/utils';

const actions = {
  async fetchFormSourceFields({ commit, dispatch }) {
    commit('setFormSourceFieldLoading', true);
    try {
      const formId = router.currentRoute.value.params.formId as string;
      const response = await fetchFormIoFieldsV2FieldMappings({
        queryParams: {
          form_id: formId
        }
      });
      commit('setFormSourceFields', response);
    } catch (error) {
      dispatch(
        'studioToast/triggerToast',
        {
          msg: `Failed to fetch Goldcast's form source fields!`,
          mode: 'error'
        },
        { root: true }
      );
      logger.error(error);
    } finally {
      commit('setFormSourceFieldLoading', false);
    }
  },

  async fetchFormFieldMappings(
    { state, commit, dispatch },
    { crmObjectType, crmType }: { crmObjectType: string; crmType: string }
  ) {
    if (state.formFieldMappings[crmObjectType].mappings.length !== 0) {
      return;
    }
    commit('setFormFieldMappingLoading', {
      crmObjectType,
      isFieldMappingLoading: true
    });
    try {
      const formId = router.currentRoute.value.params?.formId as string;
      // @ts-ignore
      const formFieldMappings: CrmField[] = await listFieldMappings({
        queryParams: {
          entity_type: 'GLOBAL_FORM',
          entity_id: formId,
          crm_type: crmType,
          crm_object_type: crmObjectType
        }
      });
      commit('setFormFieldMappings', { crmObjectType, formFieldMappings });
      commit('setPrevFormFieldMappings', {
        crmObjectType,
        prevFormFieldMappings: formFieldMappings
      });
      const defaultErrorArray = new Array(formFieldMappings.length).fill(null);
      commit('setFormFieldMappingRowError', {
        crmObjectType,
        errorsArray: defaultErrorArray
      });
    } catch (error) {
      dispatch(
        'studioToast/triggerToast',
        {
          msg: `Failed to fetch ${crmType}'s form field mappings for ${crmObjectType}!`,
          mode: 'error'
        },
        { root: true }
      );
      logger.error(error);
    } finally {
      commit('setFormFieldMappingLoading', {
        crmObjectType,
        isFieldMappingLoading: false
      });
    }
  },

  async saveBulkFormFieldMappings(context, crmObjectType) {
    context.commit('setSaveFormFieldMappingLoading', {
      crmObjectType,
      isSaveFormFieldMappingLoading: true
    });
    const formId = router.currentRoute.value.params?.formId as string;
    const formFieldMappings = context.state.formFieldMappings[crmObjectType]?.mappings;
    const userToken = context.rootState?.user?.trayUserToken;
    const crmType = context.rootState?.orgIntegration?.currentCrmType;
    const solutionInstanceId = context.rootState?.orgIntegration?.currentSolutionInstance?.id;
    try {
      await createFieldMappings({
        body: {
          entity_type: 'GLOBAL_FORM',
          entity_id: formId,
          crm_type: crmType,
          crm_object_type: crmObjectType,
          field_mappings: parseFieldMappingRequestBody(formFieldMappings),
          solution_instance_id: solutionInstanceId
        },
        headers: { 'user-token': userToken.token }
      });
      context.commit('setPrevFormFieldMappings', {
        crmObjectType,
        prevFormFieldMappings: formFieldMappings
      });
      context.dispatch(
        'studioToast/triggerToast',
        { msg: 'Form Field mapping created/updated successfully.', mode: 'success' },
        { root: true }
      );
    } catch (error) {
      const parsedError = parseErrorFromServer(error);
      if (parsedError && typeof parsedError === 'string') {
        context.dispatch('studioToast/triggerToast', { msg: parsedError, mode: 'error' }, { root: true });
      } else {
        context.dispatch(
          'studioToast/triggerToast',
          {
            msg: 'Failed to create/update form field mappings! Try again.',
            mode: 'error'
          },
          { root: true }
        );
      }
      if (Array.isArray(parsedError)) {
        context.commit('setFormFieldMappingRowError', {
          crmObjectType,
          errorsArray: parsedError
        });
      }
    } finally {
      context.commit('setSaveFormFieldMappingLoading', {
        crmObjectType,
        isSaveFormFieldMappingLoading: false
      });
    }
  },
  async createDefaultFormFieldMappings(context) {
    context.commit('setCreateDefaultFormFieldMappingsLoading', true);
    const crmType = context.rootState?.orgIntegration?.currentCrmType;
    const formId = router.currentRoute.value.params?.formId as string;
    try {
      await defaultMappingCreationV2Organization({
        body: { crm_type: crmType, entity_type: 'GLOBAL_FORM', entity_id: formId }
      });
    } catch (error) {
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: `Failed to create default field mappings for ${crmType}!`,
          mode: 'error'
        },
        { root: true }
      );
    } finally {
      context.commit('setCreateDefaultFormFieldMappingsLoading', false);
    }
  }
};

export default actions;
